import { Component, HostListener, OnInit, signal, effect } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { IconOptions, MatIconRegistry, MatIcon } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import { initializeApp } from 'firebase/app';
import { getAuth, onAuthStateChanged, User } from 'firebase/auth';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  getFirestore,
  onSnapshot,
  query,
  updateDoc,
} from 'firebase/firestore';
import { getFunctions } from 'firebase/functions';
import { getStorage } from 'firebase/storage';
import { environment } from 'src/environments/environment';
import { NavigationEnd, Router, RouterLink, RouterOutlet } from '@angular/router';
import { Location } from '@angular/common';
import {
  DataStoreExport,
  PublicSettings,
  OrgUser,
  RootUser,
  ScreenSizeExport,
  Particularity,
} from './interfaces';
import { logout, saveOrgInfoLocal } from './globals';
import { filter } from 'rxjs';
import { MatToolbar } from '@angular/material/toolbar';
import { MatButton } from '@angular/material/button';
import { MatMenuTrigger, MatMenu, MatMenuItem } from '@angular/material/menu';
import { MatProgressSpinner } from '@angular/material/progress-spinner';

export const app = initializeApp(environment.firebase);
export const db = getFirestore(app);
export const functions = getFunctions(app, 'europe-west1');
export const auth = getAuth(app);
export const storage = getStorage(app);
const initialDataStore: DataStoreExport = {
  rootUser: null,
  orgUser: null,
  selectedOrganisation: null,
  isMemberOfMultipleOrgs: null,
  publicSettings: null,
  theming: null,
  subscribes: [],
};
export const dataStoreSignal = signal<DataStoreExport>(initialDataStore);
export const dataStore: DataStoreExport = initialDataStore;
export function updateDataStore<K extends keyof DataStoreExport>(
  prop: K,
  value: DataStoreExport[K],
) {
  dataStore[prop] = value;
  dataStoreSignal.set({ ...dataStore });
}

export const screenSize: ScreenSizeExport = {
  height: null,
  width: null,
};
export interface Color {
  name: string;
  hex: string;
  darkContrast: boolean;
}

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  imports: [
    MatToolbar,
    RouterLink,
    MatButton,
    MatMenuTrigger,
    MatIcon,
    MatMenu,
    MatMenuItem,
    RouterOutlet,
    MatProgressSpinner,
  ],
})
export class AppComponent implements OnInit {
  showNavbar = signal(false);
  settingOrg = signal(false);
  myProfileButton = signal(false);
  loaded = signal(false);
  dataStore: DataStoreExport = dataStore;

  constructor(
    public titleService: Title,
    private router: Router,
    private location: Location,
    private iconRegistry: MatIconRegistry,
    private sanitizer: DomSanitizer,
  ) {
    effect(() => {
      const theming = dataStoreSignal().theming;
      if (theming?.name) {
        this.titleService.setTitle(theming.name);
      }
      const faviconEl = document.querySelector('#favicon') as HTMLLinkElement | null;
      if (faviconEl && theming?.favicon) {
        faviconEl.href = theming.favicon;
      }
    });

    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe(async () => {
        if (
          !this.location.path().includes('/auth') &&
          !this.location.path().includes('/set') &&
          !this.location.path().includes('/payment-landing') &&
          !this.location.path().includes('/pay')
        ) {
          this.showNavbar.set(true);
        } else {
          this.showNavbar.set(false);
        }
        if (this.location.path().includes('/set')) {
          this.settingOrg.set(true);
        } else {
          this.settingOrg.set(false);
        }
        if (!this.location.path().includes('/first-time')) {
          this.myProfileButton.set(true);
        } else {
          this.myProfileButton.set(false);
        }
        this.getScreenSize();
      });
  }

  @HostListener('window:resize', ['$event'])
  getScreenSize(event?) {
    screenSize.height = window.innerHeight;
    screenSize.width = window.innerWidth;
  }

  async ngOnInit() {
    onAuthStateChanged(auth, async (user: User) => {
      await auth.authStateReady();
      if (!this.location.path().includes('/auth') && user) {
        await initDataStoreRootUser(user, this.iconRegistry, this.sanitizer);
        await initDataStorePublicSettings();
        await initDataStoreOrgUser();
      }

      if (!this.settingOrg()) {
        if (!dataStore.theming) {
          await saveOrgInfoLocal(dataStore.selectedOrganisation?.id ?? environment.defaultOrgId);
        }
      }

      this.loaded.set(true);
    });
  }

  async setOrganisation(orgId: string) {
    await updateDoc(doc(db, `users/${dataStore.rootUser.id}`), {
      selectedOrgId: orgId,
    });
    location.reload();
  }

  async logout() {
    await logout(this.router);
  }
}

export async function initDataStoreRootUser(
  user: User,
  iconRegistry?: MatIconRegistry,
  sanitizer?: DomSanitizer,
) {
  dataStore.rootUser = {
    ...(await getDoc(doc(db, `users/${user.uid}`))).data(),
    id: user.uid,
  } as RootUser;
  updateDataStore('rootUser', dataStore.rootUser);

  if (dataStore.subscribes.findIndex((unsub) => unsub.id === 'rootUser') === -1) {
    const unsubRootUser = onSnapshot(doc(db, `users/${user.uid}`), async (userDoc) => {
      dataStore.rootUser = {
        ...userDoc.data(),
        id: user.uid,
      } as RootUser;
      updateDataStore('rootUser', dataStore.rootUser);
    });
    dataStore.subscribes.push({ id: 'rootUser', unsub: unsubRootUser });
  }

  dataStore.selectedOrganisation =
    dataStore.rootUser.organisations[
      dataStore.rootUser.organisations.findIndex(
        (org) => org.id === dataStore.rootUser.selectedOrgId,
      )
    ];
  updateDataStore('selectedOrganisation', dataStore.selectedOrganisation);

  dataStore.selectedOrganisation.particularities = [];
  (
    await getDocs(
      collection(db, `organisations/${dataStore.selectedOrganisation.id}/particularities`),
    )
  ).forEach((particularityDoc) => {
    const particularity = { ...particularityDoc.data(), id: particularityDoc.id } as Particularity;
    dataStore.selectedOrganisation.particularities.push(particularity);
  });
  updateDataStore('selectedOrganisation', dataStore.selectedOrganisation);

  if (dataStore.subscribes.findIndex((unsub) => unsub.id === 'particularities') === -1) {
    console.log('adding particularities subscription with id', dataStore.selectedOrganisation.id);
    const unsubParticularities = onSnapshot(
      query(collection(db, `organisations/${dataStore.selectedOrganisation.id}/particularities`)),
      (snapShot) => {
        const particularities = [];
        for (const particularityDoc of snapShot.docs) {
          const particularity = particularityDoc.data() as Particularity;
          particularities.push({
            ...particularity,
            id: particularityDoc.id,
          } as Particularity);
          const options = {} as IconOptions;
          if (particularity.viewBox) {
            options['viewBox'] =
              `0 0 ${particularityDoc.data().viewBox.width} ${particularityDoc.data().viewBox.height}`;
          }
          iconRegistry.addSvgIcon(
            particularityDoc.id,
            sanitizer.bypassSecurityTrustResourceUrl(particularity.iconUrl),
            options,
          );
        }
        particularities.sort((a, b) => a.name.localeCompare(b.name));
        dataStore.selectedOrganisation.particularities = particularities;
        updateDataStore('selectedOrganisation', dataStore.selectedOrganisation);
      },
    );

    dataStore.subscribes.push({ id: 'particularities', unsub: unsubParticularities });
  }

  dataStore.isMemberOfMultipleOrgs =
    dataStore.rootUser.organisations && dataStore.rootUser.organisations.length > 1 ? true : false;
  updateDataStore('isMemberOfMultipleOrgs', dataStore.isMemberOfMultipleOrgs);
}

export async function initDataStorePublicSettings() {
  const publicSettingsDoc = await getDoc(
    doc(db, `organisations/${dataStore.rootUser.selectedOrgId}/settings/public`),
  );
  dataStore.publicSettings = {
    ...publicSettingsDoc.data(),
    id: publicSettingsDoc.id,
  } as PublicSettings;
  updateDataStore('publicSettings', dataStore.publicSettings);

  if (dataStore.subscribes.findIndex((unsub) => unsub.id === 'publicSettings') === -1) {
    const unsubPublicSettings = onSnapshot(
      doc(db, `organisations/${dataStore.rootUser.selectedOrgId}/settings/public`),
      async (settingsDoc) => {
        dataStore.publicSettings = {
          ...settingsDoc.data(),
          id: settingsDoc.id,
        } as PublicSettings;
        updateDataStore('publicSettings', dataStore.publicSettings);
        await saveOrgInfoLocal(dataStore.selectedOrganisation?.id);
      },
    );

    dataStore.subscribes.push({
      id: 'publicSettings',
      unsub: unsubPublicSettings,
    });
  }
}

export async function initDataStoreOrgUser() {
  const orgUserDoc = await getDoc(
    doc(db, `organisations/${dataStore.rootUser.selectedOrgId}/users/${dataStore.rootUser.id}`),
  );

  dataStore.orgUser = {
    ...orgUserDoc.data(),
    id: orgUserDoc.id,
  } as OrgUser;
  updateDataStore('orgUser', dataStore.orgUser);

  if (!dataStore.orgUser.particularityIds) {
    dataStore.orgUser.particularityIds = [];
    updateDataStore('orgUser', dataStore.orgUser);
  }

  if (dataStore.subscribes.findIndex((unsub) => unsub.id === 'orgUser') === -1) {
    const unsubOrgUser = onSnapshot(
      doc(db, `organisations/${dataStore.rootUser.selectedOrgId}/users/${dataStore.rootUser.id}`),
      async (userDoc) => {
        dataStore.orgUser = {
          ...userDoc.data(),
          id: dataStore.rootUser.id,
        } as OrgUser;

        if (!dataStore.orgUser.particularityIds) {
          dataStore.orgUser.particularityIds = [];
        }
        updateDataStore('orgUser', dataStore.orgUser);
      },
    );
    dataStore.subscribes.push({
      id: 'orgUser',
      unsub: unsubOrgUser,
    });
  }
}
